import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Message, User } from '../../types';
import { Phone, Video, Info, Image, Download } from 'lucide-react';
import MessageComposer from './MessageComposer';

interface ConversationViewProps {
  selectedUser: User;
  messages: Message[];
  onSendMessage: (content: string, attachments?: File[]) => void;
}

const ConversationView = ({ selectedUser, messages, onSendMessage }: ConversationViewProps) => {
  const { user: currentUser } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const MessageBubble = ({ message }: { message: Message }) => {
    const isOwn = message.senderId === currentUser?.id;
    
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}>
        {!isOwn && (
          <div className="w-8 h-8 rounded-full overflow-hidden mr-2 flex-shrink-0">
            <img 
              src={selectedUser.avatar} 
              alt={selectedUser.name} 
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-1' : 'order-2'}`}>
          <div className={`px-4 py-2 rounded-2xl ${
            isOwn 
              ? 'bg-green-500 text-white' 
              : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600'
          }`}>
            {/* Message content */}
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
            
            {/* Attachments would go here */}
            {/* {message.attachments && message.attachments.map((attachment, index) => (
              <div key={index} className="mt-2">
                {attachment.type === 'image' ? (
                  <img src={attachment.url} alt="Attachment" className="rounded-lg max-w-full" />
                ) : (
                  <div className="flex items-center space-x-2 p-2 bg-black bg-opacity-10 rounded">
                    <Download size={16} />
                    <span className="text-sm">{attachment.name}</span>
                  </div>
                )}
              </div>
            ))} */}
          </div>
          
          <div className={`mt-1 text-xs text-gray-500 dark:text-gray-400 ${isOwn ? 'text-right' : 'text-left'}`}>
            {formatMessageTime(message.createdAt)}
            {isOwn && (
              <span className="ml-2">
                {message.read ? '✓✓' : '✓'}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
            <img 
              src={selectedUser.avatar} 
              alt={selectedUser.name} 
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">{selectedUser.name}</h3>
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              <span>En línea</span>
              {selectedUser.sport && (
                <>
                  <span className="mx-2">•</span>
                  <span>{selectedUser.sport}</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button className="p-2 rounded-full text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">
            <Phone size={18} />
          </button>
          <button className="p-2 rounded-full text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">
            <Video size={18} />
          </button>
          <button className="p-2 rounded-full text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">
            <Info size={18} />
          </button>
        </div>
      </div>
      
      {/* Messages Area */}
      <div className="flex-grow overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
        <div className="space-y-1">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Image className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 mb-2">
                Inicia una conversación con {selectedUser.name}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Los mensajes están cifrados de extremo a extremo
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))
          )}
          
          {isTyping && (
            <div className="flex justify-start mb-4">
              <div className="w-8 h-8 rounded-full overflow-hidden mr-2 flex-shrink-0">
                <img 
                  src={selectedUser.avatar} 
                  alt={selectedUser.name} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl px-4 py-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      {/* Message Composer */}
      <MessageComposer 
        onSendMessage={onSendMessage}
        disabled={disabled}
        placeholder={`Mensaje para ${selectedUser.name}...`}
      />
    </div>
  );
};

export default ConversationView;